import { TRADE_TYPE_LABELS, TRADE_TYPE_COLORS } from "./constants";

/**
 * 格式化数字为货币格式
 * @param {number} value - 要格式化的数字
 * @param {string} currency - 货币符号，默认为 '$'
 * @returns {string} 格式化后的货币字符串
 */
export const formatCurrency = (value, currency = "$") => {
  if (value === null || value === undefined) return "0";
  return `${currency}${Number(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/**
 * 格式化数字为带单位的字符串
 * @param {number} value - 要格式化的数字
 * @param {number} decimals - 小数位数，默认为 2
 * @returns {string} 格式化后的字符串
 */
export const formatNumber = (value, decimals = 2) => {
  if (value === null || value === undefined) return "0";

  const num = Number(value);
  if (num >= 1e9) {
    return (num / 1e9).toFixed(decimals) + "B";
  } else if (num >= 1e6) {
    return (num / 1e6).toFixed(decimals) + "M";
  } else if (num >= 1e3) {
    return (num / 1e3).toFixed(decimals) + "K";
  }
  return num.toFixed(decimals);
};

/**
 * 格式化百分比
 * @param {number} value - 要格式化的数字
 * @param {number} decimals - 小数位数，默认为 2
 * @returns {string} 格式化后的百分比字符串
 */
export const formatPercentage = (value, decimals = 2) => {
  if (value === null || value === undefined) return "0%";
  return `${Number(value).toFixed(decimals)}%`;
};

/**
 * 格式化日期 (YYYY-MM-DD)
 * @param {string|Date} date - 要格式化的日期
 * @returns {string} 格式化后的日期字符串
 */
export const formatDate = (date) => {
  if (!date) return "";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

/**
 * 格式化日期时间 (YYYY-MM-DD HH:mm:ss)
 * @param {string|Date} date - 要格式化的日期
 * @returns {string} 格式化后的日期时间字符串
 */
export const formatDateTime = (date) => {
  if (!date) return "";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const seconds = String(d.getSeconds()).padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

/**
 * 格式化交易量
 * @param {number} volume - 交易量
 * @returns {string} 格式化后的交易量字符串
 */
export const formatVolume = (volume) => {
  return formatNumber(volume, 2);
};

/**
 * 曲线图 tooltip 中的金额 / 成交量 / 盈亏：千分位 + 固定 2 位小数（不用 K/M 缩写）
 */
export const formatChartTooltipValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/**
 * 格式化交易所名称
 * @param {string} exchange - 交易所代码
 * @param {Array} exchangesList - 可选的交易所列表（从API获取）
 * @returns {string} 格式化后的交易所名称
 */
export const formatExchangeName = (exchange, exchangesList = null) => {
  // 如果提供了交易所列表，从中查找
  if (exchangesList && Array.isArray(exchangesList)) {
    const found = exchangesList.find((e) => e.code === exchange);
    if (found) {
      return found.name;
    }
  }

  // 默认的交易所名称映射（作为 fallback）
  const exchangeNames = {
    binance: "Binance",
    okex: "OKX",
    huobi: "Huobi",
    bitget: "Bitget",
    bybit: "Bybit",
    kucoin: "KuCoin",
    gate: "Gate.io",
    mexc: "MEXC",
    poloniex: "Poloniex",
  };
  return exchangeNames[exchange] || exchange.toUpperCase();
};

/**
 * 格式化文件大小
 * @param {number} bytes - 文件大小（字节）
 * @returns {string} 格式化后的文件大小字符串
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 B";

  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

/**
 * 格式化交易类型
 * @param {string} tradeType - 交易类型 (spot/swap)
 * @returns {string} 中文标签
 */
export const formatTradeType = (tradeType) => {
  return TRADE_TYPE_LABELS[tradeType] || tradeType;
};

/**
 * 获取交易类型颜色
 * @param {string} tradeType - 交易类型 (spot/swap)
 * @returns {string} Ant Design Tag 颜色
 */
export const getTradeTypeColor = (tradeType) => {
  return TRADE_TYPE_COLORS[tradeType] || "default";
};

export default {
  formatCurrency,
  formatNumber,
  formatPercentage,
  formatDate,
  formatDateTime,
  formatVolume,
  formatChartTooltipValue,
  formatExchangeName,
  formatFileSize,
  formatTradeType,
  getTradeTypeColor,
};
